import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import {
  getReportingEditTemplateUrl,
  matchConsolidationsRoutePath,
  matchReportingInternalRenderReportUrl
} from '@futrli/shared-services';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportTemplateView } from '@futrli/views/views/reports/ModifyReportTemplateView/ModifyReportTemplateView';
import { ReportsListView } from '@futrli/views/views/reports/ReportsListView/ReportsListView';
import { useRouteIds } from '@futrli/predict/hooks/useRouteIds';

import { CreateTemplateSidebarMode } from './create-template-sidebar';
import { useCreateTemplateForm } from './hooks/useCreateTemplateForm';
import { TemplateCreatingScreen } from './screens/template-creating-screen';
import { TemplateCreatedScreen } from './screens/template-created-screen';
import { TemplateFormScreen } from './screens/template-form-screen';

export interface CreateTemplateWizardContainerProps {
  onClose: () => void;
  mode?: CreateTemplateSidebarMode;
  preSelectedReportId?: string;
}

type Screen = 'form' | 'creating' | 'created';

export const CreateTemplateWizardContainer = ({
  onClose,
  mode = 'report',
  preSelectedReportId
}: CreateTemplateWizardContainerProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const location = useLocation();
  const { report, dashboard } = useSidebarContext();

  const { workspaceId, organisationId } = useRouteIds();

  const isConsolidation =
    !!matchConsolidationsRoutePath(location.pathname) ||
    !!matchReportingInternalRenderReportUrl(location.pathname, true);

  const view = useApiView(
    useMemo(() => new ModifyReportTemplateView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const reportsView = useApiView(
    useMemo(() => new ReportsListView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  // Form state
  const {
    templateName,
    setTemplateName,
    selectedReportId,
    setSelectedReportId,
    errors: validationErrors,
    validate,
    revalidate,
    isReportPreSelected
  } = useCreateTemplateForm({ mode, initialReportId: preSelectedReportId });

  const isDashboard = mode === 'dashboard';

  const filteredReports = useMemo(
    () =>
      isDashboard
        ? reportsView.state.dashboardReports ?? []
        : reportsView.state.reports ?? [],
    [reportsView.state.dashboardReports, reportsView.state.reports, isDashboard]
  );

  // React to view state changes for create template flow
  useEffect(() => {
    if (view.state.isCreatingTemplate) {
      setScreen('creating');
    }
  }, [view.state.isCreatingTemplate]);

  useEffect(() => {
    if (view.state.newTemplateId) {
      setScreen('created');
    }
  }, [view.state.newTemplateId]);

  useEffect(() => {
    if (view.state.errorCreatingTemplate) {
      setError(
        intl.formatMessage({
          id: 'reporting.try_again',
          defaultMessage: 'Try again.'
        })
      );
      setScreen('form');
    }
  }, [view.state.errorCreatingTemplate, intl]);

  const handleCreate = useCallback(() => {
    if (!validate()) {
      return;
    }

    setError(undefined);

    view.createTemplateFromReport({
      name: templateName.trim(),
      reportId: selectedReportId
    });
  }, [validate, view, templateName, selectedReportId]);

  const handleView = useCallback(() => {
    if (view.state.newTemplateId) {
      onClose();
      navigate(getReportingEditTemplateUrl(workspaceId, organisationId, view.state.newTemplateId, isConsolidation));
    }
  }, [view.state.newTemplateId, navigate, workspaceId, organisationId, isConsolidation, onClose]);

  const handleNavigateToCreateReport = useCallback(() => {
    onClose();
    if (isDashboard) {
      dashboard.open();
    } else {
      report.open();
    }
  }, [onClose, isDashboard, dashboard, report]);

  return (
    <>
      {screen === 'form' && (
        <TemplateFormScreen
          error={error}
          validationErrors={validationErrors}
          templateName={templateName}
          selectedReportId={selectedReportId}
          liveReports={filteredReports}
          onTemplateNameChange={setTemplateName}
          onSelectedReportIdChange={setSelectedReportId}
          onClose={onClose}
          onCreate={handleCreate}
          onBlur={revalidate}
          mode={mode}
          onNavigateToCreateReport={handleNavigateToCreateReport}
          isReportSelectDisabled={isReportPreSelected}
        />
      )}
      {screen === 'creating' && <TemplateCreatingScreen />}
      {screen === 'created' && <TemplateCreatedScreen onClose={onClose} onView={handleView} mode={mode} />}
    </>
  );
};
